/*
 * pt-booking-core.js — shared booking utilities for the 3 booking surfaces
 * (pt-booking-modal, pt-service-booking-modal, pt-booking-page).
 *
 * These are the DOM-INDEPENDENT pieces that were duplicated across all three:
 * reading the #pt-locations-data island, picking the location, the local-date
 * helper, the availability fetch, the .ics builder, the NAP formatter, and the
 * "offered at these shops only" rule a restricted service (road force
 * balancing, Edmonton Trail NE) applies to the location picker.
 *
 * Surface-specific logic (steps, fields, payload construction, price/tier rules,
 * DOM wiring) stays in each surface. Each surface builds its own payload and owns
 * its own DOM — this module only shares the stable plumbing.
 *
 * Loaded synchronously in the <head> (layout/theme.liquid) so window.PTBookingCore
 * exists before any inline booking script runs.
 *
 * Shopify serves this file MINIFIED from its CDN (comments stripped, whitespace
 * collapsed — about 45% of this source). Verify a push by behaviour in a real
 * browser or by tokens/string literals, never by exact formatting or by size.
 */
(function () {
  "use strict";

  var SW_FALLBACK = {
    address: "111 42 Ave SW, Calgary",
    addressFull: "111 42 Ave SW, Calgary, AB T2G 0A4",
    phone: "(403) 452-4283",
  };

  function pad2(n) { return (n < 10 ? "0" : "") + n; }

  var Core = {
    SW_FALLBACK: SW_FALLBACK,

    // Local YYYY-MM-DD (never UTC — toISOString would shift the day). Matches the
    // old bkLocalISO + the inline copies in the other two surfaces.
    localISO: function (d) {
      return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
    },

    // Parse the global #pt-locations-data island -> { all, bookable }. Defensive:
    // any missing island / parse error yields empty arrays so booking never breaks.
    loadLocations: function () {
      try {
        var el = document.getElementById("pt-locations-data");
        var all = el ? JSON.parse(el.textContent) : [];
        if (!Array.isArray(all)) all = [];
        return {
          all: all,
          bookable: all.filter(function (l) { return l && l.bookable === true; }),
        };
      } catch (e) {
        return { all: [], bookable: [] };
      }
    },

    bySlug: function (locs, slug) {
      for (var i = 0; i < (locs || []).length; i++) {
        if (locs[i] && locs[i].slug === slug) return locs[i];
      }
      return null;
    },

    // Pick the initial location from a list of BOOKABLE locations:
    // an explicit slug (data-location / ?location=) -> the primary shop -> first.
    pickLocation: function (bookable, slug) {
      bookable = bookable || [];
      if (slug) {
        var hit = Core.bySlug(bookable, slug);
        if (hit) return hit;
      }
      // Where a booking STARTS, which is not the same question as which shop is
      // the main business. booking_default wins; is_primary stays the fallback.
      for (var i = 0; i < bookable.length; i++) {
        if (bookable[i] && bookable[i].booking_default) return bookable[i];
      }
      for (var j = 0; j < bookable.length; j++) {
        if (bookable[j] && bookable[j].is_primary) return bookable[j];
      }
      return bookable[0] || null;
    },

    // ── Services that only some shops offer ──────────────────────────────
    // GET /api/services carries `locations` per row: [] = every shop, else the
    // slugs of the shops that offer it (the road force balancer is at Edmonton
    // Trail NE only). The three surfaces narrow their location picker to those
    // shops when such a service or add-on is chosen; /api/book refuses the
    // others, so this is the courtesy and the server is the fence.

    // The slugs a catalog row is offered at, normalised. [] = everywhere.
    serviceShops: function (row) {
      var raw = row && row.locations;
      if (!Array.isArray(raw)) return [];
      var out = [];
      for (var i = 0; i < raw.length; i++) {
        if (typeof raw[i] === "string" && raw[i].trim()) out.push(raw[i].trim().toLowerCase());
      }
      return out;
    },

    // The bookable shops that satisfy EVERY restriction in `lists` (an array of
    // slug arrays; empty lists mean "no restriction" and are ignored).
    allowedLocations: function (bookable, lists) {
      bookable = bookable || [];
      var active = (lists || []).filter(function (l) { return Array.isArray(l) && l.length; });
      if (!active.length) return bookable.slice();
      return bookable.filter(function (loc) {
        var slug = loc && loc.slug ? String(loc.slug).toLowerCase() : "";
        for (var i = 0; i < active.length; i++) {
          if (active[i].indexOf(slug) === -1) return false;
        }
        return true;
      });
    },

    // "Prince Tires — Edmonton Trail NE" -> "Edmonton Trail NE".
    shortShopName: function (loc) {
      var n = (loc && (loc.public_name || loc.name)) || "Prince Tires";
      return n.replace(/^Prince Tires\s*[—–-]\s*/i, "");
    },

    // "Edmonton Trail NE only" — the badge for a restricted service. `all` is
    // the full island list so a shop that is not bookable online still names
    // itself; falls back to the upper-cased slug.
    onlyAtLabel: function (slugs, all) {
      var names = [];
      for (var i = 0; i < (slugs || []).length; i++) {
        var hit = Core.bySlug(all || [], slugs[i]);
        names.push(hit ? Core.shortShopName(hit) : String(slugs[i]).toUpperCase());
      }
      if (!names.length) return "";
      return names.join(" or ") + " only";
    },

    // The sentence under the picker, mirroring the server's refusal wording.
    onlyAtNote: function (serviceName, slugs, all) {
      var label = Core.onlyAtLabel(slugs, all);
      if (!label) return "";
      return serviceName + " is only available at our " + label.replace(/ only$/, "") + " shop.";
    },

    // The deep-link slug from ?location= (or null).
    queryLocationSlug: function () {
      try {
        return new URLSearchParams(window.location.search).get("location");
      } catch (e) {
        return null;
      }
    },

    // NAP for a location with the SW fallback baked in — the surfaces inject these
    // into their own (differently-id'd) done cards.
    napOf: function (loc) {
      loc = loc || {};
      return {
        publicName: loc.public_name || "Prince Tires",
        address: loc.address || SW_FALLBACK.address,
        addressFull: loc.address_full || loc.address || SW_FALLBACK.addressFull,
        phone: loc.phone || SW_FALLBACK.phone,
      };
    },

    // GET availability. `apiBase` is the surface's AVAILABILITY_API constant;
    // `locationSlug` is the selected shop's slug (or falsy for single-location).
    // `extra` (optional) names the product being booked — product_handle,
    // variant_id, qty, book_type — so the server can answer that product's
    // EARLIEST bookable slot with the same rules /api/book refuses with. The
    // storefront cannot know a tire is coming from the supplier's Edmonton
    // warehouse; only the feed does. Without this, a customer was offered a day
    // the POST then refused at the last step of the form.
    //
    // Always resolves, never rejects:
    //   { booked: string[], allDayBlocked: boolean,
    //     blockedReason: string|null,            // why, when allDayBlocked
    //     earliest: {date,time,label,reason}|null, // this product's floor
    //     dropOff: {cap,taken,full}|null }       // only when extra.drop_off=1
    //
    // Drop-offs (owner, 2026-10-29): a surface asks with extra { drop_off: 1 }
    // and the server adds ONE key, dropOff, with that shop's daily cap for the
    // day. Without drop_off=1 the request URL is exactly what it was before.
    // A server that does not send the key yet gives dropOff: null — the surface
    // then lets the customer try, and /api/book is the authority.
    fetchAvailability: function (apiBase, dateISO, locationSlug, extra) {
      var url = apiBase + "?date=" + encodeURIComponent(dateISO);
      if (locationSlug) url += "&location_id=" + encodeURIComponent(locationSlug);
      if (extra) {
        Object.keys(extra).forEach(function (k) {
          var v = extra[k];
          if (v !== undefined && v !== null && v !== "") url += "&" + encodeURIComponent(k) + "=" + encodeURIComponent(v);
        });
      }
      return fetch(url)
        .then(function (r) {
          // A non-2xx is the platform refusing — a billing stop (402), a crash
          // (5xx), a rate limit (429) — not "nothing is booked today". For a
          // whole hour on 2026-10-19 every request came back 402 and this
          // function, swallowing it, drew a wide-open grid that then failed at
          // the last step. Refusals are reported as `unavailable` so the
          // surfaces can say so and point at the phone.
          if (!r.ok) throw new Error("availability HTTP " + r.status);
          return r.json();
        })
        .then(function (d) {
          var e = d && d.earliest && d.earliest.date ? d.earliest : null;
          var dk = d && d.dropOff && typeof d.dropOff === "object" ? d.dropOff : null;
          return {
            booked: Array.isArray(d && d.booked) ? d.booked : [],
            allDayBlocked: !!(d && d.allDayBlocked),
            blockedReason: (d && typeof d.blockedReason === "string" && d.blockedReason) || null,
            earliest: e ? { date: String(e.date), time: String(e.time || ""), label: String(e.label || ""), reason: String(e.reason || "") } : null,
            dropOff: dk ? { cap: Number(dk.cap) || 0, taken: Number(dk.taken) || 0, full: dk.full === true } : null,
            unavailable: false,
          };
        })
        .catch(function () { return { booked: [], allDayBlocked: false, blockedReason: null, earliest: null, dropOff: null, unavailable: true }; });
    },

    // POST a booking. The SURFACE builds the payload (shapes differ per surface);
    // this only shares the fetch mechanics. Resolves { ok, status, data }.
    postBooking: function (apiBase, payload) {
      return fetch(apiBase, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).then(function (r) {
        return r.json().then(
          function (j) { return { ok: r.ok, status: r.status, data: j }; },
          function () { return { ok: r.ok, status: r.status, data: null }; }
        );
      });
    },

    // Build an .ics string. Parameterized for the per-surface differences:
    //   { dateISO:'YYYY-MM-DD', time:'9:30 AM', durationMinutes, summary, description, location }
    // Returns the ICS string, or null if the time can't be parsed.
    //
    // { dateISO, allDay: true, summary, description, location } builds an
    // ALL-DAY event instead (a drop-off has a day, not a time: the customer
    // brings the car any time the shop is open). DTEND is the next day, as
    // RFC 5545 makes an all-day end exclusive. A timed call is unchanged.
    buildICS: function (o) {
      o = o || {};
      var esc = function (s) {
        return String(s == null ? "" : s).replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
      };
      if (o.allDay) {
        var dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(o.dateISO || ""));
        if (!dm) return null;
        var next = new Date(Date.UTC(+dm[1], +dm[2] - 1, +dm[3] + 1));
        var nd = next.getUTCFullYear() + pad2(next.getUTCMonth() + 1) + pad2(next.getUTCDate());
        return [
          "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Prince Tires//Booking//EN",
          "BEGIN:VEVENT",
          "DTSTART;VALUE=DATE:" + dm[1] + dm[2] + dm[3],
          "DTEND;VALUE=DATE:" + nd,
          "TRANSP:TRANSPARENT",
          "SUMMARY:" + esc(o.summary || "Prince Tires drop-off"),
          "LOCATION:" + esc(o.location || SW_FALLBACK.addressFull),
          "DESCRIPTION:" + esc(o.description || ""),
          "END:VEVENT", "END:VCALENDAR",
        ].join("\r\n");
      }
      var m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((o.time || "").trim());
      if (!m || !o.dateISO) return null;
      var h = parseInt(m[1], 10), min = parseInt(m[2], 10), ap = m[3].toUpperCase();
      if (ap === "PM" && h !== 12) h += 12;
      if (ap === "AM" && h === 12) h = 0;
      var ds = o.dateISO.replace(/-/g, "");
      var ts = pad2(h) + pad2(min) + "00";
      var endTotal = h * 60 + min + (o.durationMinutes || 60);
      var te = pad2(Math.floor(endTotal / 60) % 24) + pad2(endTotal % 60) + "00";
      return [
        "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Prince Tires//Booking//EN",
        "BEGIN:VEVENT",
        "DTSTART;TZID=America/Edmonton:" + ds + "T" + ts,
        "DTEND;TZID=America/Edmonton:" + ds + "T" + te,
        "SUMMARY:" + esc(o.summary || "Prince Tires appointment"),
        "LOCATION:" + esc(o.location || SW_FALLBACK.addressFull),
        "DESCRIPTION:" + esc(o.description || ""),
        "END:VEVENT", "END:VCALENDAR",
      ].join("\r\n");
    },

    // Trigger a download of an .ics string.
    downloadICS: function (ics, filename) {
      var blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
      var a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename || "prince-tires.ics";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(a.href); }, 0);
    },
  };

  window.PTBookingCore = Core;
})();
